'use client';
import { useTheme } from '@mui/material';
import React from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import { StrapiBackgroundOptions, StrapiDisplayOptions } from '@/lib/DisplayOptionsHelper';
import { useSpecialtyCalculators } from '@/lib/hooks/useSpecialtyCalculators';
import {
  ComponentBlocksLoanCalculators,
  Enum_Componentshareddisplayoptions_Contentswidth,
} from '@/types.d';

import {
  CalculatorInputs,
  CalculatorLayout,
  CalculatorOutputs,
  OutputAlert,
  OutputComparison,
  OutputField,
  OutputGraphicStackedBar,
  Styled2ColGrid,
  StyledTextSlider,
  StyledToggleButtonField,
} from './components';

type EarlyRepaymentFormValues = {
  outstandingBalance: number;
  annualRate: number;
  remainingTermYears: number;
  repaymentFrequency: 'monthly' | 'fortnightly' | 'weekly';
  lumpSumPayment: number;
  additionalPerPeriod: number;
};

const EarlyRepayment = ({ block }: { block: ComponentBlocksLoanCalculators }) => {
  const theme = useTheme();
  const { calculateEarlyRepayment } = useSpecialtyCalculators();

  const methods = useForm<EarlyRepaymentFormValues>({
    defaultValues: {
      outstandingBalance: 500000,
      annualRate: 6.5,
      remainingTermYears: 25,
      repaymentFrequency: 'monthly',
      lumpSumPayment: 10000,
      additionalPerPeriod: 500,
    },
  });
  const { control, watch } = methods;
  const values = watch();

  const result = calculateEarlyRepayment({
    outstandingBalance: values.outstandingBalance || 0,
    annualRate: values.annualRate || 0,
    remainingTermYears: values.remainingTermYears || 0,
    repaymentFrequency: values.repaymentFrequency || 'monthly',
    lumpSumPayment: values.lumpSumPayment || 0,
    additionalPerPeriod: values.additionalPerPeriod || 0,
  });

  return (
    <FormProvider {...methods}>
      <CalculatorLayout
        title={block?.CustomCalculatorTitle ?? undefined}
        blurb={block?.CalculatorBlurb}
        backgroundOptions={(block?.CalculatorBackground ?? undefined) as StrapiBackgroundOptions}
        displayOptions={
          {
            ...block?.CalculatorDisplayOptions,
            ContentsWidth:
              block?.CalculatorDisplayOptions?.ContentsWidth ??
              Enum_Componentshareddisplayoptions_Contentswidth.Wide,
            PaddingTop: block?.CalculatorDisplayOptions?.PaddingTop ?? 'md',
            PaddingBottom: block?.CalculatorDisplayOptions?.PaddingBottom ?? 'md',
            PaddingInline: block?.CalculatorDisplayOptions?.PaddingInline ?? 'sm',
          } as StrapiDisplayOptions
        }
        rowLayout={false}
        dataComponent="EarlyRepayment"
      >
        <CalculatorInputs title={block?.CustomInputsTitle ?? 'Enter loan details'}>
          <Controller
            name="outstandingBalance"
            control={control}
            render={({ field }) => (
              <StyledTextSlider
                label="Outstanding Balance"
                prefix="$"
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="annualRate"
            control={control}
            render={({ field }) => (
              <StyledTextSlider
                label="Annual Interest Rate"
                suffix="%"
                value={field.value}
                defaultMax={20}
                step={0.1}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="remainingTermYears"
            control={control}
            render={({ field }) => (
              <StyledTextSlider
                label="Remaining Term"
                suffix={field.value === 1 ? ' year' : ' years'}
                value={field.value}
                minValue={1}
                maxValue={30}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="lumpSumPayment"
            control={control}
            render={({ field }) => (
              <StyledTextSlider
                label="Lump Sum Payment"
                prefix="$"
                value={field.value}
                onChange={field.onChange}
                helperText="*One-off extra payment applied now"
              />
            )}
          />
          <Controller
            name="additionalPerPeriod"
            control={control}
            render={({ field }) => (
              <StyledTextSlider
                label="Additional Per Period"
                prefix="$"
                value={field.value}
                onChange={field.onChange}
                helperText="*Extra added to each regular payment"
              />
            )}
          />
          <Controller
            name="repaymentFrequency"
            control={control}
            render={({ field }) => (
              <StyledToggleButtonField
                label="Repayment Frequency"
                value={field.value}
                onChange={(value) =>
                  field.onChange(value as EarlyRepaymentFormValues['repaymentFrequency'])
                }
                options={[
                  { value: 'monthly', label: 'Monthly' },
                  { value: 'fortnightly', label: 'Fortnightly' },
                  { value: 'weekly', label: 'Weekly' },
                ]}
              />
            )}
          />
        </CalculatorInputs>

        <CalculatorOutputs title={block?.CustomOutputsTitle ?? 'Repayment Savings'}>
          {!result.canCalculate && (
            <OutputAlert>
              Extra payment is too small to cover periodic interest. Increase your extra payment to
              see savings.
            </OutputAlert>
          )}
          <OutputGraphicStackedBar
            sx={{
              mx: 'auto',
            }}
            categories={['Standard', 'New']}
            series={[
              {
                label: 'Outstanding Principal',
                data: [values.outstandingBalance || 0, values.outstandingBalance || 0],
                color: theme.palette.brand.primary,
              },
              {
                label: 'Interest',
                data: [result.standardTotalInterestValue, result.newTotalInterestValue],
                color: theme.palette.brand.secondary,
              },
            ]}
          />
          <OutputComparison
            columnTitles={['Standard', 'New']}
            highlightedColumn={1}
            rows={[
              {
                label: `${result.paymentLabel} Payment`,
                values: [`$${result.standardRepayment}`, `$${result.newRepaymentPerPeriod}`],
              },
              {
                label: 'Total Interest',
                values: [`$${result.standardTotalInterest}`, `$${result.newTotalInterest}`],
              },
            ]}
          />
          <Styled2ColGrid>
            <OutputField label="New Loan Term" value={result.newTermYears} />
            <OutputField label="Time Saved" value={result.timeSavedYears} />
          </Styled2ColGrid>
          <OutputField label="Interest Saved" value={`$${result.interestSaved}`} highlight />
        </CalculatorOutputs>
      </CalculatorLayout>
    </FormProvider>
  );
};

export default EarlyRepayment;
